<script setup lang="ts">
    import KFormDisplay from "@/components/forms/displays/FormDisplay/KFormDisplay.vue";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import KSection from "@/components/generic/KSection.vue";
    import KTag from "@/components/KTag.vue";
    import { useStore } from "@/store";
    import { getLockName, NllLock } from "@/store/modules/recept/helpers";
    import ReceptforskrivningDisplayPortal from "@/tabs/Receptforskrivning/Forskrivning/Display/ReceptforskrivningDisplayPortal.vue";
    import ValtLakemedelDisplay from "@/tabs/Receptforskrivning/Forskrivning/Form/ValtLakemedel/ValtLakemedelDisplay.vue";
    import { computed, toRefs } from "vue";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
        receptforskrivningId?: string;
        editingSamling?: boolean;
        // Är det en förnyelse av en förskrivning?
        fornya?: boolean;
    }>();

    const { receptforskrivningId } = toRefs(props);

    const emit = defineEmits<{
        (event: "action", payload: { id?: string; step: "form" | "display" | "list" }): void;
        (event: "cancel"): void;
    }>();

    const receptforskrivning = computed(() =>
        store.getters["receptforskrivning/receptforskrivningById"]({ id: receptforskrivningId?.value })
    );
    const lakemedel = computed(() =>
        store.getters["lakemedel/lakemedelById"]({
            id: receptforskrivning?.value?.lakemedelId,
            displayMode: true,
        })
    );

    const onAction = (payload: { id?: string; step: "form" | "display" | "list" }) => {
        emit("action", payload);
    };

    const onCancel = () => {
        emit("cancel");
    };
</script>

<template>
    <KFormDisplay :showEmptyValues="true" class="receptforskrivning-display">
        <div class="form-content">
            <div v-if="receptforskrivning?.validationMessages?.length" class="validation span-all">
                <KTag v-for="(message, index) in receptforskrivning.validationMessages" :key="index" variant="info">
                    <span>{{ message.display }}</span>
                </KTag>
            </div>
        </div>
        <KSection name="lakemedel" heading="Läkemedel" :number="1">
            <ValtLakemedelDisplay :lakemedel="lakemedel" />
        </KSection>
        <KSection name="expedieringsdetaljer" heading="Expedieringsdetaljer" :number="2">
            <div class="form-content">
                <div class="form-section span-all">
                    <KFormFieldDisplay
                        name="forpackning"
                        label="Förpackning"
                        validation="required"
                        :value="lakemedel?.forpackning"
                        showEmptyValue
                    />
                    <KFormFieldDisplay
                        name="antal_uttag"
                        label="Antal uttag"
                        validation="required"
                        :value="receptforskrivning?.antal_uttag"
                    />
                    <KFormFieldDisplay
                        name="forpackningar_per_uttag"
                        label="Förpackningar per uttag"
                        validation="required"
                        :value="receptforskrivning?.forpackningar_per_uttag"
                    />
                    <KFormFieldDisplay
                        name="sista_giltighetsdag"
                        label="Sista giltighetsdag"
                        validation="required"
                        :value="receptforskrivning?.sista_giltighetsdag"
                    />
                    <KFormFieldDisplay
                        name="startforpackning"
                        label="Startförpackning"
                        :value="receptforskrivning?.startforpackning"
                    />
                    <KFormFieldDisplay
                        name="substitution_tillaten"
                        label="Substitution tillåten"
                        :value="receptforskrivning?.substitution_tillaten"
                    />
                </div>
                <div class="form-section span-all">
                    <KFormFieldDisplay
                        name="tid_mellan_uttag"
                        label="Expedieringsintervall"
                        :value="receptforskrivning?.tid_mellan_uttag"
                    />
                    <KFormFieldDisplay
                        name="tidsenhet"
                        label="Tidsenhet"
                        :value="
                            store.getters['nll/tidsenheterById']({
                                id: receptforskrivning?.tidsenhet,
                                displayMode: true,
                            })?.label
                        "
                    />
                    <KFormFieldDisplay
                        name="forsta_uttag_fore"
                        label="Första uttag före"
                        :value="receptforskrivning?.forsta_uttag_fore"
                    />
                    <KFormFieldDisplay
                        name="forskrivarens_kommentar"
                        label="Förskrivarens kommentar"
                        :value="receptforskrivning?.forskrivarens_kommentar"
                    />
                </div>
                <KFormFieldDisplay
                    name="forskrivning_enligt_smittskyddslagen"
                    label="Förskrivning enligt smittkyddslagen"
                    :value="receptforskrivning?.forskrivning_enligt_smittskyddslagen"
                />
                <KFormFieldDisplay
                    name="formansberattigad_patient"
                    label="Förmånsberättigad patient"
                    :value="receptforskrivning?.formansberattigad_patient"
                />
                <KFormFieldDisplay
                    name="formansvillkor_for_lakemedel_uppfyllda"
                    label="Förmånsvillkor för läkemedel uppfyllda"
                    :value="receptforskrivning?.formansvillkor_for_lakemedel_uppfyllda"
                />
                <KFormFieldDisplay
                    v-if="fornya"
                    name="beskrivning_handelseorsak"
                    label="Beskrivning händelseorsak"
                    :value="receptforskrivning?.beskrivning_handelseorsak"
                />
            </div>
        </KSection>
        <KSection name="behandling" heading="Behandling" :number="3">
            <div class="form-content">
                <KFormFieldDisplay
                    name="behandlingsorsak"
                    label="Behandlingsorsak"
                    validation="required"
                    :value="
                        store.getters['nll/behandlingsorsakerById']({
                            id: receptforskrivning?.behandlingsorsak,
                            displayMode: true,
                        })?.label
                    "
                />
                <KFormFieldDisplay name="annan_orsak" label="Annan orsak" :value="receptforskrivning?.annan_orsak" />
                <KFormFieldDisplay
                    name="behandlingsandamal"
                    label="Behandlingsändamål"
                    validation="required"
                    class="span-two"
                    :value="receptforskrivning?.behandlingsandamal"
                />
                <KFormFieldDisplay
                    name="senaste_datum_for_uppfoljning_av_behandling"
                    label="Senaste datum för uppföljning av behandling"
                    class="span-two"
                    :value="receptforskrivning?.senaste_datum_for_uppfoljning_av_behandling"
                />
                <KFormFieldDisplay
                    name="senaste_datum_for_avslut_av_behandling"
                    label="Senaste datum för avslut av behandling"
                    class="span-two"
                    :value="receptforskrivning?.senaste_datum_for_avslut_av_behandling"
                />
            </div>
        </KSection>
        <KSection name="dosering" heading="Dosering" :number="4">
            <div class="form-content">
                <KFormFieldDisplay
                    name="dosering"
                    class="span-two"
                    validation="required"
                    label="Dosering"
                    :value="receptforskrivning?.dosering"
                />
                <KFormFieldDisplay
                    name="administreringsmetod"
                    label="Administreringsmetod"
                    :value="
                        store.getters['nll/administreringsmetoderById']({
                            id: receptforskrivning?.administreringsmetod,
                            displayMode: true,
                        })?.label
                    "
                />
                <KFormFieldDisplay
                    v-if="receptforskrivning?.administreringsmetod_annan"
                    name="administreringsmetod_annan"
                    label="Annan metod"
                    :value="receptforskrivning?.administreringsmetod_annan"
                />
                <KFormFieldDisplay
                    name="administreringsvag"
                    label="Administreringsväg"
                    :value="
                        store.getters['nll/administreringsvagarById']({
                            id: receptforskrivning?.administreringsvag,
                            displayMode: true,
                        })?.label
                    "
                />
                <KFormFieldDisplay
                    v-if="receptforskrivning?.administreringsvag_annan"
                    name="administreringsvag_annan"
                    label="Annan väg"
                    :value="receptforskrivning?.administreringsvag_annan"
                />
                <KFormFieldDisplay
                    name="administreringsstalle"
                    label="Administreringsställe"
                    :value="
                        store.getters['nll/administreringsstallenById']({
                            id: receptforskrivning?.administreringsstalle,
                            displayMode: true,
                        })?.label
                    "
                />
                <KFormFieldDisplay
                    v-if="receptforskrivning?.administreringsstalle_annan"
                    name="administreringsstalle_annan"
                    label="Annat ställe"
                    :value="receptforskrivning?.administreringsstalle_annan"
                />
                <KFormFieldDisplay
                    name="precisering_administreringsstalle"
                    label="Precisering administreringsställe"
                    :value="
                        store.getters['nll/preciseringarById']({
                            id: receptforskrivning?.precisering_administreringsstalle,
                            displayMode: true,
                        })?.label
                    "
                />
                <KFormFieldDisplay
                    v-if="receptforskrivning?.precisering_administreringsstalle_annan"
                    name="precisering_administreringsstalle_annan"
                    label="Annan precisering"
                    :value="receptforskrivning?.precisering_administreringsstalle_annan"
                />
                <KFormFieldDisplay
                    name="medicinteknisk_produkt"
                    label="Medicinteknisk produkt"
                    :value="
                        store.getters['nll/medicintekniskaProdukterById']({
                            id: receptforskrivning?.medicinteknisk_produkt,
                            displayMode: true,
                        })?.label
                    "
                />
                <KFormFieldDisplay
                    v-if="receptforskrivning?.medicinteknisk_produkt_annan"
                    name="medicinteknisk_produkt_annan"
                    label="Annan medicinteknisk produkt"
                    :value="receptforskrivning?.medicinteknisk_produkt_annan"
                />
                <KFormFieldDisplay
                    name="annan_instruktion"
                    label="Annan instruktion"
                    :value="receptforskrivning?.annan_instruktion"
                />
                <div class="form-section span-all">
                    <KFormFieldDisplay
                        name="forsta_doseringsdag"
                        label="Första doseringsdag"
                        :value="receptforskrivning?.forsta_doseringsdag"
                    />
                    <KFormFieldDisplay
                        name="sista_doseringsdag"
                        label="Sista doseringsdag"
                        :value="receptforskrivning?.sista_doseringsdag"
                    />
                    <KFormFieldDisplay name="vid_behov" label="Vid behov" :value="receptforskrivning?.vid_behov" />
                    <KFormFieldDisplay
                        name="obs_overstiger_maxdosering"
                        label="Obs. överstiger maxdosering"
                        :value="receptforskrivning?.obs_overstiger_maxdosering"
                    />
                </div>
            </div>
        </KSection>
        <KSection name="sparr" heading="Spärr" :number="5">
            <!-- ${value.sparr_legal_ground}-${value.sparr_subject_type}` -->
            <KFormFieldDisplay
                name="sparr"
                label="Typ av spärr"
                class="span-all"
                :value="
                    NllLock[
                        getLockName({
                            value: {
                                sparr_legal_ground: receptforskrivning?.sparr_legal_ground,
                                sparr_subject_type: receptforskrivning?.sparr_subject_type,
                            },
                        })
                    ]?.label
                "
            />
        </KSection>
        <!-- Targettar och renderar i portalen i Receptforskrivning.vue -->
        <ReceptforskrivningDisplayPortal
            :tabId="tabId"
            :receptforskrivningId="receptforskrivningId"
            @action="onAction"
            @cancel="onCancel"
            :editingSamling="editingSamling"
            :fornya="fornya"
        />
    </KFormDisplay>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .receptforskrivning-display {
        // Behövs för att portal (footern) i tabben ska hamna längst ner
        flex-grow: 1;

        .validation {
            display: grid;
            grid-auto-flow: row;
            grid-gap: 0.5rem;
            padding: 0.5rem;
            margin-bottom: 1rem;

            .message {
                display: flex;
                gap: $icon-gap-default;

                .icon-wrapper {
                    display: grid;
                    place-items: center;
                    height: $line-height-default;

                    .icon {
                        max-height: 16px;
                    }
                }
            }
        }

        .collapsed-info {
            background-color: $color-blue-light;
            padding: 1rem;
            border-radius: $border-radius-max;

            .expanded-info {
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;

                .tag {
                    display: grid;
                    align-items: center;
                    grid-auto-flow: column;
                    grid-gap: 0.4rem;
                    padding: 0.3rem 0.5rem;
                    border-radius: $border-radius-max;
                    background-color: $color-background-light;

                    &.no-background {
                        background-color: unset;
                    }

                    &:first-of-type {
                        padding-left: 0;
                    }
                }
            }
        }
    }
</style>
